import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { TaskFieldsAccent } from "@/components/tasks/details/TaskFieldsForm/TaskFieldsForm";
import { X } from "lucide-react";

type Props = {
  value: string;
  savedValue: string;
  onChange: (next: string) => void;
  onClear: () => void;
  onCancel: () => void;
  onSave: () => void;
  isSaving?: boolean;
  accent?: TaskFieldsAccent;
  headerIcon?: React.ReactNode;
};

export function TaskLogEditor({
  value,
  savedValue,
  onChange,
  onClear,
  onCancel,
  onSave,
  isSaving = false,
  accent,
  headerIcon,
}: Props) {
  const hasChanges = value !== savedValue;

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-3">
          <div
            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-muted-foreground"
            style={accent ? { color: accent.ink, opacity: 0.68 } : undefined}
          >
            {headerIcon}
            Log
          </div>
          {value && (
            <button
              type="button"
              onMouseDown={e => e.preventDefault()}
              onClick={onClear}
              className="inline-flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
              style={accent ? { color: accent.ink } : undefined}
              aria-label="Clear log"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <Textarea
          rows={Math.max(4, Math.min(12, value.split("\n").length + 1))}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder="Status change log will appear here."
          className="font-mono text-[12px] leading-relaxed"
        />
      </div>
      {hasChanges && (
        <div className="grid grid-cols-2 gap-3">
          <Button
            type="button"
            variant="ghost"
            className="h-12 bg-muted/60 hover:bg-muted"
            onClick={onCancel}
            disabled={isSaving}
            style={accent ? { color: accent.ink } : undefined}
          >
            Cancel
          </Button>
          <Button
            type="button"
            className="h-12"
            onClick={onSave}
            disabled={isSaving}
            style={accent ? { background: accent.soft, color: accent.bar } : undefined}
          >
            Save
          </Button>
        </div>
      )}
    </div>
  );
}

export function countTaskLogLines(log: string | null | undefined): number {
  return (log ?? "").split("\n").filter(Boolean).length;
}
